import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../lib/format';
import type { Role, UserDto } from '@shared/types';

const usersKey = ['users'] as const;

export default function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [], isLoading } = useQuery({
    queryKey: usersKey,
    queryFn: () => api.get<UserDto[]>('/users'),
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('user');

  const invalidate = () => qc.invalidateQueries({ queryKey: usersKey });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { role?: Role; isActive?: boolean } }) =>
      api.put<UserDto>(`/users/${id}`, body),
    onSuccess: invalidate,
  });

  const create = useMutation({
    mutationFn: () => api.post<UserDto>('/users', { email: email.trim(), role }),
    onSuccess: () => {
      setEmail('');
      setRole('user');
      invalidate();
    },
  });

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">Načítám…</div>;

  const error = create.error || update.error;

  return (
    <div>
      <h1 className="mb-6 font-heading text-3xl font-bold text-dark">Uživatelé</h1>

      {error && (
        <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">E-mail</th>
              <th className="px-4 py-3 text-left font-semibold">Jméno</th>
              <th className="px-4 py-3 text-left font-semibold">Role</th>
              <th className="px-4 py-3 text-left font-semibold">Přístup</th>
              <th className="px-4 py-3 text-left font-semibold">Poslední přihlášení</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium">
                    {u.email}
                    {isMe && <span className="ml-2 text-xs text-gray-400">(ty)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {u.name || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      className="input w-32"
                      value={u.role}
                      disabled={isMe || update.isPending}
                      onChange={(e) =>
                        update.mutate({ id: u.id, body: { role: e.target.value as Role } })
                      }
                    >
                      <option value="user">Uživatel</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={u.isActive}
                        disabled={isMe || update.isPending}
                        onChange={(e) =>
                          update.mutate({ id: u.id, body: { isActive: e.target.checked } })
                        }
                      />
                      <span className="text-gray-600">{u.isActive ? 'Aktivní' : 'Zablokován'}</span>
                    </label>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDateTime(u.lastLoginAt)}</td>
                </tr>
              );
            })}

            <tr className="border-t border-gray-100 bg-gray-50">
              <td className="px-4 py-3">
                <input
                  className="input"
                  type="email"
                  placeholder="novy@buydubai.eu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </td>
              <td className="px-4 py-3 text-xs text-gray-500">Jméno se doplní po přihlášení</td>
              <td className="px-4 py-3">
                <select
                  className="input w-32"
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                >
                  <option value="user">Uživatel</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td className="px-4 py-3" colSpan={2}>
                <button
                  className="btn-primary"
                  disabled={!email.trim() || create.isPending}
                  onClick={() => create.mutate()}
                >
                  <UserPlus size={16} /> {create.isPending ? 'Přidávám…' : 'Přidat uživatele'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        Přihlásit se může jen uživatel, který je v seznamu a má aktivní přístup. Admin navíc
        spravuje nastavení značek, šablony a uživatele.
      </p>
    </div>
  );
}
